import { AnimatedCard, FadeIn } from '../../../components/animations';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import type { LeadStats } from '../../../hooks/useLeads';
import { useTranslatedStageName } from '../../../hooks/useTranslatedStageName';
import { colors } from '../../../theme/colors';

interface PipelineSummaryProps {
  stats: LeadStats | undefined;
  isLoading: boolean;
}

export function PipelineSummary({ stats, isLoading }: Readonly<PipelineSummaryProps>) {
  const { t } = useTranslation();
  const translateStageName = useTranslatedStageName();

  if (isLoading || !stats) {
    return (
      <AnimatedCard delay={150}>
        <Box
          backgroundColor={colors.background.card}
          borderRadius="lg"
          borderWidth={1}
          borderColor={colors.border.muted}
          padding="lg"
        >
          <VStack spacing="md">
            <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
              {t('leads.pipelineSummary.title')}
            </Text>
            <Box alignItems="center" justifyContent="center" paddingY="xl">
              <Text variant="body" color={colors.text.tertiary}>
                {t('leads.pipelineSummary.loading')}
              </Text>
            </Box>
          </VStack>
        </Box>
      </AnimatedCard>
    );
  }

  const maxCount = Math.max(...stats.stagesBreakdown.map((s) => s.count), 1);

  return (
    <AnimatedCard delay={150}>
      <Box
        backgroundColor={colors.background.card}
        borderRadius="lg"
        borderWidth={1}
        borderColor={colors.border.muted}
        padding="lg"
      >
        <VStack spacing="md">
          <HStack justify="between" align="center">
            <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
              {t('leads.pipelineSummary.title')}
            </Text>
            <Box
              backgroundColor={colors.primary.muted}
              borderRadius="full"
              paddingX="md"
              paddingY="xxs"
            >
              <Text variant="caption" color={colors.primary[500]} style={{ fontWeight: '600' }}>
                {stats.totalLeads} {t('leads.pipelineSummary.total')}
              </Text>
            </Box>
          </HStack>

          {stats.stagesBreakdown.length === 0 ? (
            <Box padding="lg" alignItems="center">
              <Text variant="caption" color={colors.text.tertiary}>
                {t('leads.pipelineSummary.noLeads')}
              </Text>
            </Box>
          ) : (
            <VStack spacing="sm">
              {stats.stagesBreakdown.map((stage, index) => (
                <FadeIn key={stage.id} delay={200 + index * 60} translateY={4}>
                  <VStack spacing="xxs">
                    <HStack justify="between" align="center">
                      <HStack spacing="sm" align="center">
                        <Box
                          width={8}
                          height={8}
                          borderRadius="full"
                          backgroundColor={stage.color}
                        />
                        <Text variant="bodySmall" color={colors.text.secondary}>
                          {translateStageName(stage.name)}
                        </Text>
                      </HStack>
                      <Text
                        variant="bodySmall"
                        color={colors.text.primary}
                        style={{ fontWeight: '600' }}
                      >
                        {stage.count}
                      </Text>
                    </HStack>
                    <Box
                      backgroundColor={colors.background.secondary}
                      borderRadius="full"
                      style={{ height: 6 }}
                    >
                      <Box
                        backgroundColor={stage.color}
                        borderRadius="full"
                        style={{
                          height: 6,
                          width: `${(stage.count / maxCount) * 100}%`,
                          minWidth: stage.count > 0 ? 4 : 0,
                        }}
                      />
                    </Box>
                  </VStack>
                </FadeIn>
              ))}
            </VStack>
          )}

          {stats.sourceBreakdown.length > 0 && (
            <VStack spacing="sm">
              <Text
                variant="caption"
                color={colors.text.tertiary}
                style={{ fontWeight: '500', marginTop: 8 }}
              >
                {t('leads.pipelineSummary.bySource')}
              </Text>
              <HStack spacing="sm" style={{ flexWrap: 'wrap' }}>
                {stats.sourceBreakdown.map((source) => (
                  <Box
                    key={source.source}
                    backgroundColor={colors.background.secondary}
                    borderRadius="full"
                    paddingX="sm"
                    paddingY="xxs"
                  >
                    <Text variant="caption" color={colors.text.secondary} style={{ fontSize: 10 }}>
                      {source.source.replace('_', ' ')}: {source.count}
                    </Text>
                  </Box>
                ))}
              </HStack>
            </VStack>
          )}
        </VStack>
      </Box>
    </AnimatedCard>
  );
}
